import { FOCUSABLE_ELEMENTS, KLAVIYO_BIS_ENDPOINT, KLAVIYO_COMPANY_ID, KLAVIYO_LIST_ID } from '../config/constants';
import addClass from './dom/addClass';
import click from './dom/click';
import removeClass from './dom/removeClass';
import { lockScroll, unlockScroll } from './dom/scroll';
import { KEY_CODES, KEY_DOWN } from './keys';

const ON = 'on';
let currentTab = 1;
let focusableContent = null;

const setupCollectionBis = (variantData) => {
  const { product } = variantData;

  // Find all back in stock buttons in the current grid context
  const grid = document.querySelector(`[data-productid="${product.id}"]`);
  if (!grid) {
    return;
  }

  const addToBagButton = grid.querySelector('button.add-to-bag-button');

  if (!addToBagButton) {
    return;
  }

  const bg = document.getElementById('inquire-overlay-background');
  const bisOverlay = document.getElementById('collection-bis-overlay');
  const closeBisOverlay = bisOverlay?.querySelector('.hmr-overlay-close');

  // Only append if not already in the DOM
  if (bisOverlay && bisOverlay.parentNode !== document.body) {
    document.body.appendChild(bisOverlay);
  }

  const onKeyDown = (event) => {
    const isTabPressed = event.key === KEY_CODES.TAB;

    if (event.key === KEY_CODES.ESC) {
      closeOverlays();
      addToBagButton.focus();
      return;
    }

    if (!isTabPressed) {
      return;
    }

    if (event.shiftKey) {
      currentTab = currentTab - 1 < 0 ? focusableContent.length - 1 : currentTab - 1;
    } else {
      currentTab = currentTab === focusableContent.length - 1 ? 0 : currentTab + 1;
    }

    event.preventDefault();
    focusableContent[currentTab].focus();
  };

  const onBisSubmit = async (event) => {
    event.preventDefault();
    const button = bisOverlay.querySelector('button[type="submit"]');
    const innerHTML = button.innerHTML;

    const email = document.getElementById('collection-bis-email');
    const phone = document.getElementById('collection-bis-phone');
    const variantId = document.getElementById('collection-bis-variant-id');

    button.setAttribute('disabled', 'disabled');
    button.innerHTML = 'Submitting...';

    // Klaviyo Back in Stock API format (2024-06-15)
    const profileAttributes = {
      email: email.value,
    };

    const channels = ['EMAIL'];

    // Add phone number if provided
    if (phone.value && phone.value.trim()) {
      // Format phone number to international format
      let phoneNumber = phone.value.trim();

      // Remove all non-digit characters except +
      phoneNumber = phoneNumber.replace(/[^\d+]/g, '');

      // If it doesn't start with +, assume it's a US number and add +1
      if (!phoneNumber.startsWith('+')) {
        // Remove any leading 1 if present
        if (phoneNumber.startsWith('1')) {
          phoneNumber = phoneNumber.substring(1);
        }
        // Add +1 prefix for US numbers
        phoneNumber = `+1${phoneNumber}`;
      }

      // Validate the format (should be + followed by country code and number)
      if (phoneNumber.length >= 10 && phoneNumber.startsWith('+')) {
        profileAttributes.phone_number = phoneNumber;
        channels.push('SMS');
      }
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
          channels: channels,
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId.value}`,
            },
          },
        },
      },
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15',
      },
      body: JSON.stringify(payload),
    };

    try {
      const request = await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${KLAVIYO_COMPANY_ID}`, requestOptions);

      if (request.ok) {
        // If SMS is included, we need to also subscribe to a list for SMS consent
        if (channels.includes('SMS')) {
          const smsConsentPayload = {
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: profileAttributes,
                  },
                },
                custom_source: 'Back in Stock',
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: KLAVIYO_LIST_ID,
                  },
                },
              },
            },
          };

          const smsRequestOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-06-15',
            },
            body: JSON.stringify(smsConsentPayload),
          };

          try {
            await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${KLAVIYO_COMPANY_ID}`, smsRequestOptions);
          } catch (smsError) {
            console.warn('SMS consent subscription failed:', smsError);
            // Don't fail the entire request if SMS consent fails
          }
        }

        const success = bisOverlay.querySelector('.field-success');
        addClass(success, ON);
        success.focus();
      } else {
        const errorData = await request.json();
        console.error('Klaviyo BIS API error:', errorData);
        alert('There was an error signing up for back in stock notifications. Please get in touch.');
      }
    } catch (error) {
      console.error('Error submitting to Klaviyo:', error);
      alert('There was an error signing up for back in stock notifications. Please get in touch.');
    }

    button.removeAttribute('disabled');
    button.innerHTML = innerHTML;
  };

  const closeOverlays = () => {
    if (bisOverlay) {
      const bisElements = [...bisOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
      bisElements.forEach((element) => {
        element.setAttribute('tabindex', -1);
      });
      bisOverlay.querySelector('form')?.removeEventListener('submit', onBisSubmit);
      closeBisOverlay?.removeEventListener(click(), closeOverlays);
    }

    window.removeEventListener(KEY_DOWN, onKeyDown);
    bg?.removeEventListener(click(), closeOverlays);
    removeClass(bg, ON);
    removeClass(bisOverlay, ON);
    unlockScroll();
  };

  const openBis = (variantData) => {
    const { product, variant } = variantData;
    lockScroll();

    if (variant) {
      // Update the hidden fields with current variant information
      const variantIdField = document.getElementById('collection-bis-variant-id');
      if (variantIdField) variantIdField.value = variant.id;

      // Update the overlay content with variant information
      const image = document.getElementById('collection-bis-image');
      const title = document.getElementById('collection-bis-title');
      const material = document.getElementById('collection-bis-material');
      const color = document.getElementById('collection-bis-color');
      const size = document.getElementById('collection-bis-size');
      const price = document.getElementById('collection-bis-price');

      if (variant && product) {
        if (image && variant.image) {
          image.src = variant.image.src;
          image.alt = `A detail image of ${product.title || 'this product'}`;
        }
        if (title) title.textContent = product.title || 'Product';
        if (material && variant.material) material.textContent = variant.material;
        if (color && variant.color) color.textContent = variant.color;
        if (size && variant.size) size.textContent = `Size: ${variant.size}`;
        if (price && variant.price) {
          const formattedPrice = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(variant.price / 100);
          price.textContent = formattedPrice;
        }
      }
    }

    const success = bisOverlay.querySelector('.field-success');
    removeClass(success, ON);

    addClass(bg, ON);
    addClass(bisOverlay, ON);

    const elements = [...bisOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
    elements.forEach((element) => {
      element.setAttribute('tabindex', 0);
    });

    currentTab = 1;
    focusableContent = elements;
    focusableContent[0].focus();

    bisOverlay.querySelector('form').addEventListener('submit', onBisSubmit);
    closeBisOverlay.addEventListener(click(), closeOverlays);
  };

  const openOverlay = (event) => {
    // Find the button element (in case event.target is a child element)
    const button = event.target.closest('button');

    // Check if this is a back in stock button (data-bis="true")
    if (button?.dataset.bis === 'true') {
      // Prevent default and stop propagation to avoid conflicts
      event.preventDefault();
      event.stopPropagation();

      // Get the current variant data for this grid
      const grid = button.closest('.collection-grid-product');
      if (grid) {
        const productId = grid.dataset.productid;
        const colorEl = grid.querySelector('.collection-grid-color.on');
        const sizeEl = grid.querySelector('.size-select select');

        const color = colorEl?.parentElement.dataset.color || false;
        const size = sizeEl?.value || false;

        // Find the current product and variant
        const currentProduct = window.HMR?.products?.[productId];
        if (currentProduct) {
          const { variants } = currentProduct;
          let currentVariant = null;

          if (color && size) {
            currentVariant = variants.find((vari) => color === vari.color && size === vari.size);
          } else if (color) {
            currentVariant = variants.find((vari) => color === vari.color);
          } else if (size) {
            currentVariant = variants.find((vari) => size === vari.size);
          } else {
            currentVariant = variants[0];
          }

          if (currentVariant) {
            openBis({ product: currentProduct, variant: currentVariant });
            bg.addEventListener(click(), closeOverlays);
            window.addEventListener(KEY_DOWN, onKeyDown);
          }
        }
      }
    }
    // If it's an add-to-bag button without data-bis, let the addToCart function handle it
  };

  // Always attach the event listener, regardless of current button state
  addToBagButton?.addEventListener(click(), openOverlay);
};

export default setupCollectionBis;
